import { bucketContaining, type AgentTaskBucket } from './agent-task-buckets';
import { bucketIsCancellable } from './task-cancel';

/**
 * WHAT a task offers the reader, and which one of those is the button.
 *
 * Two rules meet here, and keeping them in one pure function is the point — the task detail (both
 * layouts), the list row's overflow menu, and the standalone task page all have to offer the same set or
 * an action exists on one surface and not another.
 *
 * 1. The Maestro action is chosen by the task's BUCKET, not by a status list: the user's turn asks to
 *    RESOLVE, the agent's turn can only be DISCUSSED, and a finished one is a FOLLOW-UP. Saying "Resolve"
 *    over a task the agent is still working on offers a resolution nobody can give.
 * 2. A task whose own text names the next step (today: a browse session paused for a human) promotes THAT
 *    step to the button — going to the Maestro to be handed a link this screen already holds is a detour.
 *    Only the resolve buckets are displaced this way; "Discuss" / "Follow up" are conversations ABOUT the
 *    task rather than a step waiting to be taken. The displaced Maestro action is not lost — it moves into
 *    the menu, so the handoff is always reachable.
 *
 * Cancel is menu-only, on every surface. It is destructive and not undoable, so it must never sit at
 * button weight beside the action the reader came to take.
 *
 * Mirrored 1:1 by iOS `TaskActions.swift` and Android `TaskActions.kt`.
 */
export type TaskActionKind = 'openBrowser' | 'resolve' | 'discuss' | 'followUp' | 'cancel';

export interface TaskAction {
  kind: TaskActionKind;
  /** i18n key; the four Maestro labels carry an `{agent}` token the render site fills. */
  labelKey: string;
  destructive?: boolean;
}

/** The Maestro action each bucket asks for. `resolve` is the only kind a known action displaces. */
const BUCKET_ACTION: Record<AgentTaskBucket, TaskAction> = {
  waiting: { kind: 'resolve', labelKey: 'tasks.detail.resolveWith' },
  inProgress: { kind: 'discuss', labelKey: 'tasks.detail.discussWith' },
  done: { kind: 'followUp', labelKey: 'tasks.detail.followUpWith' },
  failed: { kind: 'resolve', labelKey: 'tasks.detail.resolveWith' },
};

const OPEN_BROWSER: TaskAction = { kind: 'openBrowser', labelKey: 'tasks.detail.openBrowser' };
const CANCEL: TaskAction = { kind: 'cancel', labelKey: 'tasks.cancel.action', destructive: true };

export interface TaskActionSet {
  /** The one action drawn as the primary button. */
  primary: TaskAction;
  /** The rest, in menu order. Never contains `primary`. */
  menu: TaskAction[];
  /** Everything on offer, primary first — what a list row's overflow menu renders. */
  all: TaskAction[];
}

export function taskActions(status: string | undefined, hasKnownAction: boolean): TaskActionSet {
  const bucket = bucketContaining(status);
  const maestro = BUCKET_ACTION[bucket];
  const displaced = hasKnownAction && maestro.kind === 'resolve';

  const primary = displaced ? OPEN_BROWSER : maestro;
  const menu: TaskAction[] = [];
  if (displaced) menu.push(maestro);
  if (bucketIsCancellable(bucket)) menu.push(CANCEL);

  return { primary, menu, all: [primary, ...menu] };
}
